/* MorningRescueModal — quick-access ADHD morning rescue card deck.
 * Use when child is stuck. Swipe through strategies, log what worked. */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Heart, ThumbsUp, ThumbsDown, Sparkles, Loader2, Sun, Settings as SettingsIcon, Plus, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MorningRescueModal({ kidId, kidName, onClose }) {
  const [catalogue, setCatalogue] = useState([]);
  const [strategies, setStrategies] = useState([]); // active filtered
  const [topStats, setTopStats] = useState([]);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [profile, setProfile] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/imorgon/morning/strategies`, { withCredentials: true }),
        axios.get(`${API}/imorgon/morning/stats?kid_id=${kidId}`, { withCredentials: true }),
      ]);
      const cat = r1.data?.catalogue || [];
      const kid = (r1.data?.kids || []).find((k) => k.id === kidId);
      const activeIds = kid?.profile?.strategies || cat.map((c) => c.id);
      setProfile(kid?.profile || { strategies: activeIds, custom_strategies: [] });
      // Show only active strategies; top-performing first
      const top = r2.data?.top_strategies || [];
      const topIds = new Set(top.map((t) => t.id));
      const activeSet = new Set(activeIds);
      const activeCat = cat.filter((c) => activeSet.has(c.id));
      const sorted = [
        ...activeCat.filter((c) => topIds.has(c.id)),
        ...activeCat.filter((c) => !topIds.has(c.id)),
      ];
      // Add user's custom strategies at the end
      const customs = (kid?.profile?.custom_strategies || []).map((c, i) => ({
        id: `custom-${i}`, emoji: '✨', title: c.title, text: c.text, custom: true,
      }));
      setCatalogue(cat);
      setStrategies([...sorted, ...customs]);
      setTopStats(top);
    } catch (e) {
      toast.error('Kunde inte ladda strategier');
    } finally { setLoading(false); }
  }, [kidId]);

  useEffect(() => { load(); }, [load]);

  const log = async (worked) => {
    const s = strategies[idx];
    if (!s || s.custom) {
      // For customs we don't have a strategy_id in DB — just advance
      toast(worked ? '🌸 Snyggt!' : 'Vi provar nästa');
      setIdx((i) => Math.min(i + 1, strategies.length - 1));
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/imorgon/morning/event`, {
        kid_id: kidId,
        strategy_id: s.id,
        worked,
      }, { withCredentials: true });
      toast(worked ? '🌸 Loggat — den funkade!' : '📝 Loggat — vi lär oss');
      if (worked) {
        // Slight delay then close (success — Freja klar)
        setTimeout(() => onClose?.(), 1100);
      } else {
        setIdx((i) => Math.min(i + 1, strategies.length - 1));
      }
    } catch (e) {
      toast.error('Kunde inte logga');
    } finally { setBusy(false); }
  };

  const current = strategies[idx];
  const isLast = idx >= strategies.length - 1;

  return (
    <div className="fixed inset-0 z-[120] bg-gradient-to-br from-amber-100 via-rose-100 to-violet-100 flex flex-col" data-testid="morning-rescue">
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sun className="w-5 h-5 text-amber-600" />
          <div>
            <div className="text-xs uppercase font-extrabold text-amber-700 tracking-widest">Morgon-läge</div>
            <div className="text-sm font-bold text-amber-900">{kidName || 'Barn'} behöver hjälp</div>
          </div>
        </div>
        <button onClick={onClose} data-testid="morning-close" className="p-2 rounded-full bg-white/60 hover:bg-white text-amber-800"><X className="w-4 h-4" /></button>
      </div>

      {/* Settings shortcut */}
      <div className="px-4 pb-1">
        <button onClick={() => setSettingsOpen(true)} data-testid="morning-customize" className="text-[11px] text-amber-700 underline hover:text-amber-900 font-bold flex items-center gap-1">
          <SettingsIcon className="w-3 h-3" /> Anpassa {kidName}s strategier
        </button>
      </div>

      {/* Top-performing strip */}
      {topStats.length > 0 && idx === 0 && (
        <div className="px-4 pb-2">
          <p className="text-[11px] text-amber-800 font-bold">⭐ Funkar oftast på {kidName}:</p>
          <div className="flex gap-1.5 mt-1 flex-wrap">
            {topStats.map((t, i) => (
              <span key={i} className="text-[11px] bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full font-bold">{t.emoji} {t.title} <span className="opacity-70">{t.rate}%</span></span>
            ))}
          </div>
        </div>
      )}

      {/* Progress bar */}
      <div className="px-4">
        <div className="h-1.5 bg-amber-200/60 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-amber-400 via-rose-400 to-violet-400 transition-all" style={{ width: `${((idx + 1) / Math.max(strategies.length, 1)) * 100}%` }} />
        </div>
        <div className="text-[10px] text-amber-700 text-center mt-1 font-bold">{idx + 1} / {strategies.length} strategier</div>
      </div>

      {/* Card */}
      <div className="flex-1 flex items-center justify-center p-4">
        {loading ? (
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        ) : !current ? (
          <div className="text-center text-amber-800 max-w-sm">
            <Heart className="w-12 h-12 mx-auto fill-rose-200 text-rose-300 mb-2" />
            <p className="text-base font-extrabold">Du gör så bra du kan 💞</p>
            <p className="text-xs italic mt-1">Vi har gått igenom alla. Ta en djup andetag — det här är inte ditt fel.</p>
            <button onClick={onClose} className="mt-4 px-5 py-2.5 rounded-2xl bg-amber-500 text-white font-bold">Stäng</button>
          </div>
        ) : (
          <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl border-4 border-amber-200 p-6 text-center" data-testid="strategy-card">
            <div className="text-6xl mb-3">{current.emoji}</div>
            <h2 className="text-2xl font-extrabold text-amber-900 mb-2">{current.title}</h2>
            <p className="text-sm text-gray-700 leading-relaxed">{current.text}</p>
            {current.custom && (
              <p className="text-[10px] uppercase font-bold text-violet-600 mt-3 tracking-widest">Din egen strategi ✨</p>
            )}
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => log(false)}
                disabled={busy}
                data-testid="strategy-fail"
                className="flex-1 py-3 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-extrabold flex items-center justify-center gap-1.5 text-sm"
              >
                <ThumbsDown className="w-4 h-4" /> Funkade inte
              </button>
              <button
                onClick={() => log(true)}
                disabled={busy}
                data-testid="strategy-win"
                className="flex-1 py-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-extrabold flex items-center justify-center gap-1.5 text-sm shadow"
              >
                <ThumbsUp className="w-4 h-4" /> Den funkade!
              </button>
            </div>
            {!isLast && (
              <button
                onClick={() => setIdx((i) => i + 1)}
                data-testid="strategy-skip"
                className="mt-3 text-xs text-amber-700 underline hover:text-amber-900"
              >
                Hoppa över — nästa strategi
              </button>
            )}
          </div>
        )}
      </div>

      <p className="text-[10px] text-amber-700 italic text-center pb-3 px-4">Du är inte ensam. Det här är inte ditt fel. 💛</p>

      {settingsOpen && profile && (
        <StrategyCustomizer
          kidId={kidId}
          kidName={kidName}
          catalogue={catalogue}
          profile={profile}
          onClose={() => setSettingsOpen(false)}
          onSaved={(p) => { setProfile(p); setSettingsOpen(false); load(); }}
        />
      )}
    </div>
  );
}

// ============== STRATEGY CUSTOMIZER ==============
function StrategyCustomizer({ kidId, kidName, catalogue, profile, onClose, onSaved }) {
  const [active, setActive] = useState(new Set(profile.strategies || []));
  const [customs, setCustoms] = useState(profile.custom_strategies || []);
  const [newTitle, setNewTitle] = useState('');
  const [newText, setNewText] = useState('');
  const [saving, setSaving] = useState(false);

  const toggle = (id) => {
    setActive((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const addCustom = () => {
    if (!newTitle.trim()) return;
    setCustoms((c) => [...c, { title: newTitle.trim().slice(0, 50), text: newText.trim().slice(0, 200) }]);
    setNewTitle(''); setNewText('');
  };

  const removeCustom = (i) => {
    setCustoms((c) => c.filter((_, idx) => idx !== i));
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/imorgon/morning/profile`, {
        kid_id: kidId,
        strategies: Array.from(active),
        custom_strategies: customs,
      }, { withCredentials: true });
      toast.success('Sparat 🌸');
      onSaved?.(r.data);
    } catch (e) {
      toast.error('Kunde inte spara');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[130] bg-black/60 backdrop-blur flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <div className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-amber-400 to-rose-400 px-5 py-3 text-white flex justify-between items-center">
          <h3 className="font-extrabold flex items-center gap-2">
            <SettingsIcon className="w-4 h-4" /> {kidName}s strategier
          </h3>
          <button onClick={onClose} data-testid="customizer-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <h4 className="text-xs uppercase font-extrabold text-amber-700 tracking-wide mb-2">Aktiva strategier</h4>
            <p className="text-[11px] text-gray-500 italic mb-2">Slå på de som BRUKAR funka för {kidName}. De andra göms.</p>
            <div className="space-y-1.5">
              {catalogue.map((s) => (
                <label
                  key={s.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 cursor-pointer transition ${
                    active.has(s.id) ? 'bg-amber-50 border-amber-300' : 'bg-white border-gray-200 opacity-60'
                  }`}
                  data-testid={`customizer-strategy-${s.id}`}
                >
                  <input
                    type="checkbox"
                    checked={active.has(s.id)}
                    onChange={() => toggle(s.id)}
                    className="accent-amber-500"
                  />
                  <span className="text-xl">{s.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-amber-900">{s.title}</div>
                    <div className="text-[10px] text-gray-600 truncate">{s.text}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-200 pt-3">
            <h4 className="text-xs uppercase font-extrabold text-violet-700 tracking-wide mb-2">Egna strategier ✨</h4>
            <p className="text-[11px] text-gray-500 italic mb-2">Saker som funkar just på {kidName} men inte finns i listan.</p>
            <div className="space-y-1.5">
              {customs.map((c, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-violet-50 border border-violet-200" data-testid={`customizer-custom-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-violet-900">{c.title}</div>
                    <div className="text-[10px] text-gray-600">{c.text}</div>
                  </div>
                  <button onClick={() => removeCustom(i)} className="text-rose-500 hover:text-rose-700 p-1"><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1.5 bg-violet-50/50 rounded-xl p-2 border border-violet-100">
              <input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Titel — t.ex. 'Pussa nallen'"
                data-testid="customizer-new-title"
                className="w-full px-2.5 py-1.5 rounded-lg border border-violet-200 text-xs"
              />
              <textarea
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                placeholder="Hur man gör (frivilligt)"
                rows={2}
                data-testid="customizer-new-text"
                className="w-full px-2.5 py-1.5 rounded-lg border border-violet-200 text-xs"
              />
              <button
                onClick={addCustom}
                data-testid="customizer-add"
                disabled={!newTitle.trim()}
                className="w-full py-1.5 rounded-lg bg-violet-500 hover:bg-violet-600 text-white text-xs font-bold disabled:opacity-50 flex items-center justify-center gap-1"
              >
                <Plus className="w-3 h-3" /> Lägg till
              </button>
            </div>
          </div>

          <button
            onClick={save}
            disabled={saving}
            data-testid="customizer-save"
            className="w-full py-2.5 rounded-2xl bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white font-extrabold disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Spara'}
          </button>
        </div>
      </div>
    </div>
  );
}
